import * as React from 'react';
// Import hooks.
import {
	useAddressGeocoding,
	UseAddressGeocodingResults,
} from '@app/hooks/view';
import {
	useInputYesNo,
	UseInputYesNoResults,
} from '@app/hooks/view/input';
// Import props.
import {
	ProviderAddressChildrenProps,
	SubformInputSettingsPropertyAddressProps,
	SubformPropertyAddressProps,
} from '@app/props';
// Import api models.
import {
	AddressLocation,
	AddressLocationAsMailingAddress,
	GpsCoordinates,
    PossibleState,
} from '@app/models/api';
// Import enumerations
import { BroadLineOfBusiness } from '@app/enumerations';

interface UseSubformPropertyAddressParameters {
	id: string;
	allowEdits: boolean;
	broadLineOfBusinessCode: BroadLineOfBusiness;
	inputSettingsProps?: SubformInputSettingsPropertyAddressProps;
	showValidationsOnMount?: boolean;
	possibleStates: PossibleState[];
	propertyAddress?: AddressLocationAsMailingAddress
	onRequestToChangeLocation?: () => void;
	onFinishVerifyAddress: (address: AddressLocationAsMailingAddress, coordinates: GpsCoordinates) => void;
	addressProps: ProviderAddressChildrenProps;
}

interface UseSubformPropertyAddressResults {
	areAllInputsDisabled: boolean;
	isCommercial: boolean;
	showAddressInput: boolean;
	showCopyToMailingAddressInput: boolean;
	addressGeocoding: UseAddressGeocodingResults;
	copyToMailingAddressInput: UseInputYesNoResults;
	applySubformPropertyAddressProps: () => SubformPropertyAddressProps;
	verifyAddress: () => void;
	flagAllInputsToShowValidations: () => void;
	doInputsHaveChanges: () => boolean;
}

const useSubformPropertyAddress = (params: UseSubformPropertyAddressParameters): UseSubformPropertyAddressResults => {

	// #region exported methods

	const applySubformPropertyAddressProps = (): SubformPropertyAddressProps => {
		return {
			isCommercial,
			showAddressInput,
			showCopyToMailingAddressInput,
			addressGeocoding,
			copyToMailingAddressInput,
		};
	}

	/**
	 * Starts the submission process without validation for the address. 
	 **/
	const verifyAddress = (): void => {
		// Proceed forward if form validates successfully.
		try {
			if (addressGeocoding.addressVerification.addressLocationInput.value.address !== undefined) {
				// If the input address components are already provided, then we need to verify them as address components.
				addressGeocoding
					.addressVerification
					.verifyAddressAsComponentsAsync(addressGeocoding.addressVerification.addressLocationInput.value.address);
			}
			else if (!String.isNullOrWhiteSpace(addressGeocoding.addressVerification.addressLocationInput.value.addressSingleLine)) {
				// If only a single line address input is provided, then we need to verify that first.
				addressGeocoding
					.addressVerification
					.verifyAddressAsSingleLineAsync(addressGeocoding.addressVerification.addressLocationInput.value.addressSingleLine);
			}
		}
		catch (error: any) {
			console.log(error);
		}
	}

	/**
	 * Flags all inputs to show the validation messages. 
	 **/
	const flagAllInputsToShowValidations = (): void => {
		addressGeocoding.addressVerification.addressLocationInput.changeShowValidationFlag(true);
		copyToMailingAddressInput.changeShowValidationFlag(true);
	}

	/**
	 * Checks to see if the form has changes. 
	 **/
	const doInputsHaveChanges = (): boolean => {
		return addressGeocoding.addressVerification.addressLocationInput.doesInputHaveChanges
			|| copyToMailingAddressInput.doesInputHaveChanges;
	}

	// #endregion exported methods


	// #region private methods

	/**
	 * Finishes and saves the address.
	 * @param address
	 */
	const finishConfirmAddressGeocoding = (address: AddressLocation, coordinates: GpsCoordinates): void => {
		params.onFinishVerifyAddress({
			...address,
			copyToMailingAddress: copyToMailingAddressInput.value ?? false,
		}, coordinates);
	}

	/**
	 * Generates id. 
	 **/
	const generateId = (): string => {
		return params.id;
	}

	/**
	 * Generates id for the address. 
	 **/
	const generateIdForAddress = (): string => {
		return `${generateId()}-address`;
	}

	/**
	 * Generates id for the copyToMailingAddress. 
	 **/
	const generateIdForCopyToMailingAddress = (): string => {
		return `${generateId()}-copyToMailingAddress`;
	}

	// #endregion private methods

	// #region variables

	const showValidation: boolean = React.useMemo(() => params.showValidationsOnMount ?? false, [params.showValidationsOnMount]);

	const isCommercial: boolean = React.useMemo(() => {
		return params.broadLineOfBusinessCode === BroadLineOfBusiness.COMMERCIAL;
	}, [params.broadLineOfBusinessCode]);

	// #region Input settings visibility.

	const showAddressInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.addressInput !== 'hidden',
		[params.inputSettingsProps?.addressInput]
	);

	const showCopyToMailingAddressInput: boolean = React.useMemo(() =>
		params.inputSettingsProps?.copyToMailingAddressInput !== 'hidden',
		[params.inputSettingsProps?.copyToMailingAddressInput]
	);

	// #endregion Input settings visibility.

	// #region Input settings disable.

	const isAddressInputDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !showAddressInput
		|| params.inputSettingsProps?.addressInput === 'disabled',
		[
			params.allowEdits,
			showAddressInput,
			params.inputSettingsProps?.addressInput
		]
	);

	const isCopyToMailingAddressDisabled: boolean = React.useMemo(() =>
		!params.allowEdits
		|| !isCommercial
		|| !showCopyToMailingAddressInput
		|| params.inputSettingsProps?.copyToMailingAddressInput === 'disabled',
		[
			params.allowEdits,
			isCommercial,
			showCopyToMailingAddressInput,
			params.inputSettingsProps?.copyToMailingAddressInput
		]
	);

	// #endregion Input settings disable.

	// #region Input hooks.

	// Instantiate the 'address geocoding' hook.
	const addressGeocoding: UseAddressGeocodingResults = useAddressGeocoding({
		addressVerificationParameters: {
			addressLocationInputParameters: {
				addressInputParameters: {
					id: generateIdForAddress(),
					label: 'Enter Address, City, State, Zip',
					required: true,
					disabled: isAddressInputDisabled,
					showValidation,
					initialInput: {
						address: params.propertyAddress?.address ?? undefined,
						addressSingleLine: String.empty(),
					},
					possibleStates: params.possibleStates,
					onGetAddressSuggestions: params.addressProps.getAddressSuggestions,
				},
				addressLocation: params.propertyAddress?.gpsCoordinates ?? undefined,
				requestToChangeLocation: params.onRequestToChangeLocation,
			},
			possibleStates: params.possibleStates,
			ignorePossibleStateRiskAllowed: false,
			initialInput: undefined,
			verifySingleLineAddress: params.addressProps.verifySingleLineAddress,
			verifyAddress: params.addressProps.verifyAddress,
		},
		finishConfirmAddressGeocoding: finishConfirmAddressGeocoding,
		geocodeAddress: params.addressProps.geocodeAddress,
		geocodedAddressResult: undefined,
	});

	addressGeocoding.addressVerification.addressLocationInput
	// Instantiate the 'copyToMailingAddress' input hook.
	const copyToMailingAddressInput: UseInputYesNoResults = useInputYesNo({
		id: generateIdForCopyToMailingAddress(),
		label: 'Do you want to use this as your mailing address?',
		infoLabel: 'Answering Yes will replace any existing mailing address.',
		required: true,
		disabled: isCopyToMailingAddressDisabled,
		showValidation,
		isYesChecked: params.propertyAddress?.copyToMailingAddress ?? undefined,
	});

	// #endregion Input hooks.

	// Check to see if all inputs are disabled.
	const areAllInputsDisabled: boolean = React.useMemo(() => {
		return addressGeocoding.addressVerification.addressLocationInput.isDisabled
			&& copyToMailingAddressInput.isDisabled;
	}, [
		addressGeocoding.addressVerification.addressLocationInput.isDisabled,
		copyToMailingAddressInput.isDisabled,
	]);

	// #endregion variables

	return {
		areAllInputsDisabled,
		isCommercial,
		showAddressInput,
		showCopyToMailingAddressInput,
		addressGeocoding,
		copyToMailingAddressInput,
		applySubformPropertyAddressProps,
		verifyAddress,
		flagAllInputsToShowValidations,
		doInputsHaveChanges,
	};
}

// Export parameters.
export {
	UseSubformPropertyAddressParameters,
}

// Export results.
export {
	UseSubformPropertyAddressResults,
}

export default useSubformPropertyAddress;